<template>
  <div class="bancas-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-building"></i>
        Bancas
      </h1>
      <p class="page-subtitle">Gerencie as bancas do sistema</p>
    </div>

    <!-- Navegação por tabs -->
    <div class="tabs-container">
      <nav class="modern-tabs">
        <button 
          class="tab-button" 
          :class="{ active: selectedTab === 1 }" 
          @click="goToListagem()"
        >
          <i class="fa-solid fa-list"></i>
          LISTAGEM
        </button>
        <button 
          class="tab-button" 
          :class="{ active: selectedTab === 2 }" 
          @click="currentBanca.id ? selectedTab = 2 : createBanca()"
        >
          <i class="fa-solid fa-plus tab-plus-icon"></i>
          {{ currentBanca.id ? 'EDITAR' : 'CADASTRO' }}
        </button>
      </nav>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando...</span>
      </div>
    </div>

    <!-- Tab 1: Listagem -->
    <div v-show="selectedTab === 1 && !loading" class="tab-content">
      <div class="table-card">
        <!-- Cabeçalho da tabela -->
        <div class="table-header">
          <div class="search-container">
            <div class="search-input-group">
              <i class="fa-solid fa-search search-icon"></i>
              <input 
                type="text" 
                v-model="quickFilter" 
                class="search-input" 
                placeholder="Pesquisar bancas..." 
                autocomplete="off"
              />
            </div>
          </div>
          <span v-if="totalRows > 0" class="table-count">({{ totalRows }} banca(s) encontrada(s))</span>
          <div v-if="totalPages > 1" class="pagination-info">
            <span>Página {{ pageIndex }} de {{ totalPages }}</span>
            <div class="pagination-controls">
              <button 
                class="btn-pagination" 
                @click="changePage(pageIndex - 1)"
                :disabled="pageIndex <= 1"
              >
                <i class="fa-solid fa-chevron-left"></i>
              </button>
              <button 
                class="btn-pagination" 
                @click="changePage(pageIndex + 1)"
                :disabled="pageIndex >= totalPages"
              >
                <i class="fa-solid fa-chevron-right"></i>
              </button>
            </div>
          </div>
          <button 
            type="button" 
            class="btn-primary" 
            @click="createBanca()"
          >
            <i class="fa-solid fa-plus"></i>
            Adicionar Banca
          </button>
        </div>

        <!-- Filtros -->
        <div class="filters-section">
          <div class="filter-group">
            <label>Status:</label>
            <select v-model="filters.isActive" class="filter-select">
              <option value="">Todos</option>
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
          </div>
          <div class="filter-actions">
            <button class="btn-secondary" @click="searchBancas">
              <i class="fa-solid fa-search"></i>
              Buscar
            </button>
            <button class="btn-secondary" @click="clearFilters">
              <i class="fa-solid fa-times"></i>
              Limpar
            </button>
          </div>
        </div>

        <!-- Tabela -->
        <div class="table-container">
          <table class="modern-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nome</th>
                <th>Status</th>
                <th>Regiões</th>
                <th>Responsáveis</th>
                <th>Criado em</th>
                <th width="140">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="banca in paginatedBancas" 
                :key="banca.id"
                class="table-row"
              >
                <td>
                  <span class="status-badge badge-code">{{ formatBancaCode(banca.code) }}</span>
                </td>
                <td class="name-column">
                  <div class="banca-info">
                    <span class="banca-name">{{ banca.name }}</span>
                    <span v-if="banca.isRedescarga" class="badge-redescarga" title="Banca de Redescarga">Redescarga</span>
                  </div>
                </td>
                <td>
                  <span :class="['status-badge', banca.isActive ? 'badge-success' : 'badge-danger']">
                    {{ banca.isActive ? 'Ativo' : 'Inativo' }}
                  </span>
                </td>
                <td>
                  <div class="regions-list dropdown-hover-container" @mouseenter="showDropdown" @mouseleave="scheduleHideDropdown">
                    <span v-if="banca.regions && banca.regions.length > 0" class="regions-count hover-trigger">
                      {{ banca.regions.length }} região(ões)
                    </span>
                    <span v-else class="no-data">Nenhuma região</span>
                    
                    <!-- Dropdown de regiões -->
                    <div v-if="banca.regions && banca.regions.length > 0" class="dropdown-content regions-dropdown" @mouseenter="cancelHideDropdown" @mouseleave="scheduleHideDropdown">
                      <div class="dropdown-header">
                        <i class="fa-solid fa-map-location-dot"></i>
                        Regiões da Banca
                      </div>
                      <div class="dropdown-list">
                        <div v-for="region in banca.regions" :key="region.id" class="dropdown-item">
                          <i class="fa-solid fa-location-dot"></i>
                          {{ region.name }}
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  <div class="users-list dropdown-hover-container" @mouseenter="showDropdown" @mouseleave="scheduleHideDropdown">
                    <span v-if="banca.responsibleUsers && banca.responsibleUsers.length > 0" class="users-count hover-trigger">
                      {{ banca.responsibleUsers.length }} responsável(eis)
                    </span>
                    <span v-else class="no-data">Nenhum responsável</span>
                    
                    <!-- Dropdown de responsáveis -->
                    <div v-if="banca.responsibleUsers && banca.responsibleUsers.length > 0" class="dropdown-content users-dropdown" @mouseenter="cancelHideDropdown" @mouseleave="scheduleHideDropdown">
                      <div class="dropdown-header">
                        <i class="fa-solid fa-users"></i>
                        Usuários Responsáveis
                      </div>
                      <div class="dropdown-list">
                        <div v-for="user in banca.responsibleUsers" :key="user.id" class="dropdown-item">
                          <div class="user-info">
                            <i class="fa-solid fa-user"></i>
                            <div class="user-details">
                              <span class="user-name">{{ user.name }}</span>
                              <span class="user-role">{{ user.roleText }}</span>
                              <span v-if="user.username" class="user-username">{{ user.username }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
                <td>{{ formatDate(banca.createdAt) }}</td>
                <td>
                  <div class="action-buttons">
                    <button 
                      class="btn-action btn-edit" 
                      @click="editBanca(banca)"
                      title="Editar"
                    >
                      <i class="fa-solid fa-pen-to-square"></i>
                    </button>
                    <button 
                      class="btn-action btn-delete" 
                      @click="deleteBanca(banca.id)"
                      title="Excluir"
                    >
                      <i class="fa-solid fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="filteredBancas.length === 0" class="no-data">
                <td colspan="7">
                  <div class="no-data-message">
                    <i class="fa-solid fa-building"></i>
                    <span>Nenhuma banca encontrada</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="totalPages > 1" class="pagination-footer">
          <div class="pagination-info">
            <span>Mostrando {{ startItem }}-{{ endItem }} de {{ totalRows }} resultados</span>
          </div>
          <div class="pagination-controls">
            <button 
              class="btn-pagination" 
              @click="changePage(1)"
              :disabled="pageIndex <= 1"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button 
              class="btn-pagination" 
              @click="changePage(pageIndex - 1)"
              :disabled="pageIndex <= 1"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <span class="page-info">{{ pageIndex }} / {{ totalPages }}</span>
            <button 
              class="btn-pagination" 
              @click="changePage(pageIndex + 1)"
              :disabled="pageIndex >= totalPages"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button 
              class="btn-pagination" 
              @click="changePage(totalPages)"
              :disabled="pageIndex >= totalPages"
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Tab 2: Formulário -->
    <div v-show="selectedTab === 2" class="tab-content">
      <div class="form-card">
        <div class="form-header">
          <h3 class="form-title">
            <i class="fa-solid fa-building"></i>
            {{ currentBanca.id ? 'Editar Banca' : 'Nova Banca' }}
            <p class="form-subtitle">
              {{ currentBanca.id ? 'Atualize as informações da banca' : 'Preencha as informações da nova banca' }}
            </p>
          </h3>
        </div>

        <div class="form-body">
          <div class="form-grid">
            <!-- Código da Banca -->
            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-hashtag"></i>
                Código
              </label>
              <input 
                class="form-input banca-code-input" 
                type="text" 
                :value="formatBancaCode(currentBanca.code)"
                readonly
                disabled
                title="Código sequencial gerado automaticamente"
              />
              <div class="input-hint">
                Código sequencial gerado automaticamente (ex.: 0001).
              </div>
            </div>

            <!-- Nome da Banca -->
            <div class="input-group">
              <label class="input-label required">
                <i class="fa-solid fa-building"></i>
                Nome da Banca
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="currentBanca.name"
                placeholder="Digite o nome da banca..."
              />
            </div>

            <!-- Status -->
            <div v-if="currentBanca.id" class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-toggle-on"></i>
                Status
              </label>
              <select v-model="currentBanca.isActive" class="form-input">
                <option :value="true">Ativo</option>
                <option :value="false">Inativo</option>
              </select>
            </div>

            <!-- Banca de Redescarga -->
            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-download"></i>
                Tipo de Banca
              </label>
              <div class="checkbox-container">
                <input 
                  class="form-checkbox" 
                  type="checkbox" 
                  id="bancaIsRedescarga" 
                  v-model="currentBanca.isRedescarga" 
                />
                <label class="checkbox-label" for="bancaIsRedescarga">
                  Banca de Redescarga
                </label>
              </div>
              <div class="input-hint">
                Bancas de redescarga apenas recebem descargas; não enviam.
              </div>
            </div>

            <!-- Usuários Responsáveis -->
            <div class="input-group full-width">
              <label class="input-label">
                <i class="fa-solid fa-users"></i>
                Usuários Responsáveis
              </label>
              
              <!-- Lista de usuários selecionados -->
              <div v-if="selectedResponsibleUsers.length > 0" class="selected-users">
                <div v-for="userId in selectedResponsibleUsers" :key="userId" class="selected-user-item">
                  <span class="user-name">{{ getUserName(userId) }}</span>
                  <button 
                    type="button" 
                    class="btn-remove-user-inline"
                    @click="removeResponsibleUser(userId)"
                    title="Remover usuário"
                  >
                    <i class="fa-solid fa-times"></i>
                  </button>
                </div>
              </div>

              <!-- Adicionar novo usuário com filtro -->
              <div class="add-user-inline">
                <div class="user-filter-container">
                  <div class="user-filter-input-wrapper">
                    <i class="fa-solid fa-search user-filter-icon"></i>
                    <input
                      type="text"
                      v-model="userFilterTerm"
                      class="form-input user-select-inline"
                      placeholder="Digite para filtrar e selecionar um usuário..."
                      @focus="showUserFilterList = true"
                      @blur="hideUserFilterList"
                      autocomplete="off"
                    />
                  </div>

                  <div v-if="showUserFilterList" class="user-filter-list">
                    <div v-if="filteredUsersToAdd.length === 0" class="user-filter-empty">
                      <i class="fa-solid fa-user-slash"></i>
                      <span>Nenhum usuário encontrado</span>
                    </div>
                    <div
                      v-for="user in filteredUsersToAdd"
                      :key="user.id"
                      class="user-filter-item"
                      @mousedown.prevent="selectResponsibleUser(user)"
                    >
                      <i class="fa-solid fa-user"></i>
                      <div class="user-filter-info">
                        <span class="user-filter-name">{{ user.name }}</span>
                        <span class="user-filter-username">{{ user.username }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <button 
                  type="button" 
                  class="btn-quick-add" 
                  @click="openUserModal"
                  title="Criar usuário rapidamente"
                >
                  <i class="fa-solid fa-plus"></i>
                  Criar Usuário
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="form-footer">
          <button 
            type="button" 
            class="btn-primary" 
            @click="currentBanca.id ? updateBanca() : insertBanca()"
            :disabled="saving"
          >
            <i v-if="saving" class="fa-solid fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-save"></i>
            {{ saving ? 'Salvando...' : (currentBanca.id ? 'Atualizar' : 'Salvar') }}
          </button>
          <button 
            type="button" 
            class="btn-secondary" 
            @click="reset()"
            :disabled="saving"
          >
            <i class="fa-solid fa-times"></i>
            Cancelar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Criar Usuário Rápido -->
    <div v-if="showUserModal" class="modal-overlay" @click.self="closeUserModal">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3>
            <i class="fa-solid fa-user-plus"></i>
            Criar Usuário Rapidamente
          </h3>
          <button class="btn-close-modal" @click="closeUserModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="modal-form-grid">
            <div class="modal-input-group">
              <label class="input-label required">
                <i class="fa-solid fa-user"></i>
                Nome
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="newUser.name"
                placeholder="Digite o nome completo..."
                @keyup.enter="saveUser"
              />
            </div>

            <div class="modal-input-group">
              <label class="input-label required">
                <i class="fa-solid fa-at"></i>
                Nome de Usuário
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="newUser.username"
                placeholder="Digite o nome de usuário..."
                @keyup.enter="saveUser"
                autocomplete="off"
              />
            </div>

            <div class="modal-input-group">
              <label class="input-label">
                <i class="fa-solid fa-phone"></i>
                Telefone
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="newUser.phone"
                placeholder="Digite o telefone..."
                @keyup.enter="saveUser"
              />
            </div>

            <div class="modal-input-group">
              <label class="input-label required">
                <i class="fa-solid fa-user-tag"></i>
                Cargo
              </label>
              <select v-model="newUser.role" class="form-input">
                <option value="">Selecione o cargo</option>
                <option value="0">ADMIN</option>
                <option value="1">REGIÃO</option>
                <option value="2">COBRADOR</option>
                <option value="3">BANCA</option>
                <option value="4">AGENTE</option>
              </select>
            </div>

            <div class="modal-input-group">
              <label class="input-label required">
                <i class="fa-solid fa-lock"></i>
                Senha
              </label>
              <input 
                class="form-input" 
                type="password" 
                v-model="newUser.password"
                placeholder="Digite a senha..."
                @keyup.enter="saveUser"
                autocomplete="new-password"
              />
            </div>
            
            <div class="modal-input-group full-width">
              <div class="info-box">
                <i class="fa-solid fa-info-circle"></i>
                <span>O usuário será automaticamente associado a esta banca após a criação.</span>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button 
            type="button" 
            class="btn-primary" 
            @click="saveUser"
            :disabled="savingUser"
          >
            <i v-if="savingUser" class="fa-solid fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-save"></i>
            {{ savingUser ? 'Salvando...' : 'Salvar' }}
          </button>
          <button 
            type="button" 
            class="btn-secondary" 
            @click="closeUserModal"
            :disabled="savingUser"
          >
            <i class="fa-solid fa-times"></i>
            Cancelar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal: Criar Região Rápida -->
    <div v-if="showRegionModal" class="modal-overlay" @click.self="closeRegionModal">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3>
            <i class="fa-solid fa-map-location-dot"></i>
            Criar Região Rapidamente
          </h3>
          <button class="btn-close-modal" @click="closeRegionModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        
        <div v-if="loadingRegionModal" class="modal-loading">
          <i class="fa-solid fa-spinner fa-spin"></i>
          <span>Carregando dados...</span>
        </div>
        
        <div v-else class="modal-body">
          <div class="modal-form-grid-single">
            <div class="modal-input-group">
              <label class="input-label required">
                <i class="fa-solid fa-tag"></i>
                Nome da Região
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="newRegion.name"
                placeholder="Digite o nome da região..."
                @keyup.enter="saveRegion"
              />
            </div>

            <div class="modal-input-group">
              <label class="input-label">
                <i class="fa-solid fa-user-tie"></i>
                Responsável
              </label>
              <div class="autocomplete-container">
                <input 
                  class="form-input autocomplete-input" 
                  type="text" 
                  v-model="regionResponsibleSearchTerm"
                  @input="searchRegionResponsibles"
                  @focus="showRegionResponsibleDropdown = true"
                  @blur="hideRegionResponsibleDropdown"
                  placeholder="Digite o nome do responsável..."
                  :readonly="selectedRegionResponsible !== null"
                  autocomplete="off"
                />
                
                <!-- Botão para limpar seleção -->
                <button 
                  v-if="selectedRegionResponsible" 
                  type="button" 
                  class="clear-selection-btn"
                  @click="clearRegionResponsibleSelection"
                >
                  <i class="fa-solid fa-times"></i>
                </button>

                <!-- Dropdown de resultados -->
                <div 
                  v-if="showRegionResponsibleDropdown && regionResponsiblesResults.length > 0" 
                  class="autocomplete-dropdown"
                >
                  <div 
                    v-for="responsible in regionResponsiblesResults" 
                    :key="responsible.id"
                    class="autocomplete-item"
                    @mousedown="selectRegionResponsible(responsible)"
                  >
                    <div class="responsible-info">
                      <span class="responsible-name">{{ responsible.name }}</span>
                      <span class="responsible-username">{{ responsible.username }}</span>
                    </div>
                  </div>
                </div>

                <!-- Estado vazio -->
                <div 
                  v-if="showRegionResponsibleDropdown && regionResponsiblesResults.length === 0 && regionResponsibleSearchTerm && !loadingRegionResponsibles" 
                  class="autocomplete-empty"
                >
                  <i class="fa-solid fa-search"></i>
                  <span>Nenhum responsável encontrado</span>
                </div>

                <!-- Loading -->
                <div 
                  v-if="loadingRegionResponsibles" 
                  class="autocomplete-loading"
                >
                  <i class="fa-solid fa-spinner fa-spin"></i>
                  <span>Buscando responsáveis...</span>
                </div>
              </div>
            </div>

            <div class="modal-input-group">
              <label class="input-label">
                <i class="fa-solid fa-link"></i>
                Destino da Descarga
              </label>
              <div class="autocomplete-container">
                <input 
                  class="form-input autocomplete-input" 
                  type="text" 
                  v-model="regionDescargaSearchTerm"
                  @input="searchRegionDescarga"
                  @focus="loadAllRegionDescarga"
                  @blur="hideRegionDescargaDropdown"
                  placeholder="Digite o nome da região de descarga..."
                  :readonly="selectedRegionDescarga !== null"
                  autocomplete="off"
                />
                
                <!-- Botão para limpar seleção -->
                <button 
                  v-if="selectedRegionDescarga" 
                  type="button" 
                  class="clear-selection-btn"
                  @click="clearRegionDescargaSelection"
                >
                  <i class="fa-solid fa-times"></i>
                </button>

                <!-- Dropdown de resultados -->
                <div 
                  v-if="showRegionDescargaDropdown && regionDescargaResults.length > 0" 
                  class="autocomplete-dropdown"
                >
                  <div 
                    v-for="region in regionDescargaResults" 
                    :key="region.id"
                    class="autocomplete-item"
                    @mousedown="selectRegionDescarga(region)"
                  >
                    <div class="region-info-dropdown">
                      <span class="region-name-dropdown">{{ region.name }}</span>
                      <span class="region-type-dropdown">{{ region.onlyDescarga ? 'Descarga' : 'Normal' }}</span>
                    </div>
                  </div>
                </div>

                <!-- Estado vazio -->
                <div 
                  v-if="showRegionDescargaDropdown && regionDescargaResults.length === 0 && regionDescargaSearchTerm && !loadingRegionDescarga" 
                  class="autocomplete-empty"
                >
                  <i class="fa-solid fa-search"></i>
                  <span>Nenhuma região encontrada</span>
                </div>

                <!-- Loading -->
                <div 
                  v-if="loadingRegionDescarga" 
                  class="autocomplete-loading"
                >
                  <i class="fa-solid fa-spinner fa-spin"></i>
                  <span>Buscando regiões...</span>
                </div>
              </div>
            </div>

            <div class="modal-input-group">
              <label class="input-label">
                <i class="fa-solid fa-truck"></i>
                Configurações da Região
              </label>
              
              <div class="checkbox-group">
                <label class="checkbox-container">
                  <input 
                    type="checkbox" 
                    v-model="newRegion.onlyDescarga"
                    class="checkbox-input"
                  />
                  <span class="checkbox-checkmark"></span>
                  <span class="checkbox-label">Esta é uma região de descarga</span>
                </label>
                <div class="checkbox-description">
                  <i class="fa-solid fa-info-circle"></i>
                  <span>Regiões de descarga são utilizadas para controle específico de operações de descarga.</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button 
            type="button" 
            class="btn-primary" 
            @click="saveRegion"
            :disabled="savingRegion"
          >
            <i v-if="savingRegion" class="fa-solid fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-save"></i>
            {{ savingRegion ? 'Salvando...' : 'Salvar' }}
          </button>
          <button 
            type="button" 
            class="btn-secondary" 
            @click="closeRegionModal"
            :disabled="savingRegion"
          >
            <i class="fa-solid fa-times"></i>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import jsFunctions from "@/services/jsFunctions";

export default {
  name: "ViewBancas",
  data() {
    return {
      quickFilter: "",
      bancas: [],
      users: [],
      regions: [],
      selectedTab: 1,
      pageIndex: 1,
      pageSize: 10,
      loading: true,
      saving: false,
      currentBanca: {
        id: null,
        code: null,
        name: "",
        isActive: true,
        isRedescarga: false,
        regionIds: [],
        responsibleUserIds: []
      },
      filters: {
        isActive: ""
      },
      selectedResponsibleUsers: [],
      selectedRegions: [],
      selectedNewUserInline: "",
      selectedNewRegionInline: "",
      userFilterTerm: "",
      showUserFilterList: false,
      hideTimeout: null,
      // Modais
      showUserModal: false,
      showRegionModal: false,
      savingUser: false,
      savingRegion: false,
      newUser: {
        name: "",
        username: "",
        phone: "",
        role: "",
        password: ""
      },
      // IDs dos usuários criados pelo modal que precisam ser associados à banca
      usersCreatedInModal: [],
      newRegion: {
        name: "",
        responsibleId: null,
        descargaRegionId: null,
        onlyDescarga: false
      },
      // Autocomplete para responsável da região
      regionResponsibleSearchTerm: "",
      regionResponsiblesResults: [],
      selectedRegionResponsible: null,
      showRegionResponsibleDropdown: false,
      loadingRegionResponsibles: false,
      regionResponsibleSearchTimeout: null,
      // Autocomplete para região de descarga
      regionDescargaSearchTerm: "",
      regionDescargaResults: [],
      allRegionDescarga: [],
      selectedRegionDescarga: null,
      showRegionDescargaDropdown: false,
      loadingRegionDescarga: false,
      // Loading do modal de região
      loadingRegionModal: false
    };
  },
  computed: {
    filteredBancas() {
      let filtered = this.bancas;
      
      // Filtro rápido por nome ou código
      if (this.quickFilter) {
        const filter = this.quickFilter.toUpperCase();
        filtered = filtered.filter(banca => 
          (banca.name && banca.name.toUpperCase().includes(filter)) ||
          this.formatBancaCode(banca.code).includes(filter) ||
          String(banca.code || '').includes(this.quickFilter.trim())
        );
      }
      
      return [...filtered].sort((a, b) => (a.code || 0) - (b.code || 0));
    },

    totalRows() {
      return this.filteredBancas.length;
    },

    totalPages() {
      return Math.ceil(this.totalRows / this.pageSize) || 0;
    },

    startItem() {
      return this.totalRows === 0 ? 0 : (this.pageIndex - 1) * this.pageSize + 1;
    },

    endItem() {
      if (this.totalRows === 0) return 0;
      return Math.min(this.pageIndex * this.pageSize, this.totalRows);
    },

    paginatedBancas() {
      const start = (this.pageIndex - 1) * this.pageSize;
      return this.filteredBancas.slice(start, start + this.pageSize);
    },
    
    availableUsersToAdd() {
      return this.users.filter(user => !this.selectedResponsibleUsers.includes(user.id));
    },

    filteredUsersToAdd() {
      const users = this.availableUsersToAdd;
      if (!this.userFilterTerm) {
        return users;
      }

      const searchTerm = this.userFilterTerm.toLowerCase().trim();
      return users.filter(user =>
        (user.name && user.name.toLowerCase().includes(searchTerm)) ||
        (user.username && user.username.toLowerCase().includes(searchTerm))
      );
    },
    
    availableRegionsToAdd() {
      return this.regions.filter(region => !this.selectedRegions.includes(region.id));
    }
  },
  watch: {
    quickFilter() {
      this.pageIndex = 1;
    },
    totalPages(newTotal) {
      if (this.pageIndex > newTotal && newTotal > 0) {
        this.pageIndex = newTotal;
      } else if (newTotal === 0) {
        this.pageIndex = 1;
      }
    }
  },
  async mounted() {
    this.loading = true;
    try {
      await this.loadUsers();
      await this.loadRegions();
      await this.searchBancas();
    } catch (error) {
      this.loading = false;
      toast.error(handleErrorMessage(error));
    }
    
    // Esconder dropdowns ao rolar a página
    window.addEventListener('scroll', this.hideAllDropdowns);
    window.addEventListener('resize', this.hideAllDropdowns);
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.hideAllDropdowns);
    window.removeEventListener('resize', this.hideAllDropdowns);
  },

  methods: {
    notifyHeaderSelectsRefresh({ bancas = true, regions = true } = {}) {
      window.dispatchEvent(new CustomEvent('headerSelectsNeedRefresh', {
        detail: { bancas, regions }
      }));
    },

    async loadUsers() {
      const response = await mainService.searchUsers({});
      this.users = response.data || [];
    },

    async loadRegions() {
      const response = await mainService.searchRegions();
      this.regions = response.data || [];
    },

    async searchBancas() {
      this.loading = true;
      try {
        const request = {
          isActive: this.filters.isActive !== "" ? this.filters.isActive === "true" : null
        };

        const response = await mainService.searchBancas(request);
        this.bancas = response.data || [];
        this.pageIndex = 1;
      } catch (error) {
        toast.error(handleErrorMessage(error));
      } finally {
        this.loading = false;
      }
    },

    changePage(newPage) {
      if (newPage >= 1 && newPage <= this.totalPages) {
        this.pageIndex = newPage;
      }
    },

    clearFilters() {
      this.filters = {
        isActive: ""
      };
      this.quickFilter = "";
      this.pageIndex = 1;
      this.searchBancas();
    },

    async createBanca() {
      let nextCode = 1;
      try {
        const response = await mainService.getNextBancaCode();
        nextCode = response.data || 1;
      } catch (error) {
        const maxCode = this.bancas.reduce((max, banca) => Math.max(max, banca.code || 0), 0);
        nextCode = maxCode + 1;
      }

      this.currentBanca = {
        id: null,
        code: nextCode,
        name: "",
        isActive: true,
        isRedescarga: false,
        regionIds: [],
        responsibleUserIds: []
      };
      
      this.selectedResponsibleUsers = [];
      this.selectedRegions = [];
      this.selectedNewUserInline = "";
      this.selectedNewRegionInline = "";
      this.userFilterTerm = "";
      this.showUserFilterList = false;
      this.usersCreatedInModal = [];
      this.selectedTab = 2;
    },

    goToListagem() {
      this.selectedTab = 1;
      // Limpa edição para o próximo CADASTRO usar a mesma função do Adicionar Banca
      if (this.currentBanca.id) {
        this.currentBanca = {
          id: null,
          code: null,
          name: "",
          isActive: true,
          isRedescarga: false,
          regionIds: [],
          responsibleUserIds: []
        };
        this.selectedResponsibleUsers = [];
        this.selectedRegions = [];
        this.selectedNewUserInline = "";
        this.selectedNewRegionInline = "";
        this.userFilterTerm = "";
        this.showUserFilterList = false;
        this.usersCreatedInModal = [];
      }
    },

    editBanca(banca) {
      this.currentBanca = {
        id: banca.id || null,
        code: banca.code || null,
        name: banca.name || "",
        isActive: banca.isActive !== undefined ? banca.isActive : true,
        isRedescarga: banca.isRedescarga === true,
        regionIds: banca.regionIds || [],
        responsibleUserIds: banca.responsibleUserIds || []
      };
      
      this.selectedResponsibleUsers = [...(banca.responsibleUserIds || [])];
      this.selectedRegions = [...(banca.regionIds || [])];
      this.selectedNewUserInline = "";
      this.selectedNewRegionInline = "";
      this.userFilterTerm = "";
      this.showUserFilterList = false;
      this.selectedTab = 2;
    },

    reset() {
      this.currentBanca = {
        id: null,
        code: null,
        name: "",
        isActive: true,
        isRedescarga: false,
        regionIds: [],
        responsibleUserIds: []
      };
      
      this.selectedResponsibleUsers = [];
      this.selectedRegions = [];
      this.selectedNewUserInline = "";
      this.selectedNewRegionInline = "";
      this.userFilterTerm = "";
      this.showUserFilterList = false;
      this.usersCreatedInModal = []; // Limpar lista de usuários criados no modal
      this.selectedTab = 1;
    },

    validateBanca() {
      if (!this.currentBanca.name || this.currentBanca.name.trim() === "") {
        toast.error("Nome da banca é obrigatório!");
        return false;
      }
      
      return true;
    },

    async insertBanca() {
      if (this.saving) return;

      if (this.validateBanca()) {
        this.saving = true;
        try {
          const bancaToSend = {
            ...this.currentBanca,
            responsibleUserIds: this.selectedResponsibleUsers,
            regionIds: this.selectedRegions
          };
          
          const response = await mainService.insertBanca(bancaToSend);
          
          // Associar usuários criados no modal à banca recém-criada
          if (response.data && response.data.id && this.usersCreatedInModal.length > 0) {
            await this.associateUsersToBanca(response.data.id, this.usersCreatedInModal);
            toast.success("Banca criada e usuários associados com sucesso!");
          } else {
            toast.success("Banca criada com sucesso!");
          }
          
          this.reset();
          await this.searchBancas();
          this.notifyHeaderSelectsRefresh({ bancas: true, regions: true });
        } catch (error) {
          toast.error(handleErrorMessage(error));
        } finally {
          this.saving = false;
        }
      }
    },

    async updateBanca() {
      if (this.saving) return;

      if (this.validateBanca()) {
        this.saving = true;
        try {
          const bancaToSend = {
            ...this.currentBanca,
            responsibleUserIds: this.selectedResponsibleUsers,
            regionIds: this.selectedRegions
          };
          
          await mainService.updateBanca(bancaToSend);
          
          // Associar usuários criados no modal à banca (caso esteja editando)
          if (this.currentBanca.id && this.usersCreatedInModal.length > 0) {
            await this.associateUsersToBanca(this.currentBanca.id, this.usersCreatedInModal);
            toast.success("Banca atualizada e usuários associados com sucesso!");
          } else {
            toast.success("Banca atualizada com sucesso!");
          }
          
          this.reset();
          await this.searchBancas();
          this.notifyHeaderSelectsRefresh({ bancas: true, regions: true });
        } catch (error) {
          toast.error(handleErrorMessage(error));
        } finally {
          this.saving = false;
        }
      }
    },

    deleteBanca(id) {
      this.$confirm({
        message: "Confirma a exclusão desta banca?",
        button: { no: "Não", yes: "Sim" },
        callback: async (confirm) => {
          if (confirm) {
            try {
              await mainService.deleteBanca(id);
              toast.success("Banca excluída com sucesso!");
              await this.searchBancas();
              this.notifyHeaderSelectsRefresh({ bancas: true, regions: true });
            } catch (error) {
              toast.error(handleErrorMessage(error));
            }
          }
        },
      });
      setTimeout(() => {
        document.getElementsByClassName("vc-btn left")[0].focus();
      }, 1);
    },

    formatDate(date) {
      return jsFunctions.setDateDisplay(date) || '-';
    },

    formatBancaCode(code) {
      if (code === null || code === undefined || code === '') return '----';
      return String(code).padStart(4, '0');
    },

    getUserName(userId) {
      const user = this.users.find(u => u.id === userId);
      return user ? user.name : 'Usuário não encontrado';
    },

    getRegionName(regionId) {
      const region = this.regions.find(r => r.id === regionId);
      return region ? region.name : 'Região não encontrada';
    },

    addResponsibleUser() {
      if (this.selectedNewUserInline && !this.selectedResponsibleUsers.includes(this.selectedNewUserInline)) {
        this.selectedResponsibleUsers.push(this.selectedNewUserInline);
        this.selectedNewUserInline = "";
        this.userFilterTerm = "";
        this.showUserFilterList = false;
      }
    },

    selectResponsibleUser(user) {
      if (!user?.id || this.selectedResponsibleUsers.includes(user.id)) {
        return;
      }

      this.selectedResponsibleUsers.push(user.id);
      this.userFilterTerm = "";
      this.showUserFilterList = false;
      this.selectedNewUserInline = "";
    },

    hideUserFilterList() {
      setTimeout(() => {
        this.showUserFilterList = false;
      }, 150);
    },

    removeResponsibleUser(userId) {
      const index = this.selectedResponsibleUsers.indexOf(userId);
      if (index > -1) {
        this.selectedResponsibleUsers.splice(index, 1);
      }
    },

    addRegion() {
      if (this.selectedNewRegionInline && !this.selectedRegions.includes(this.selectedNewRegionInline)) {
        this.selectedRegions.push(this.selectedNewRegionInline);
        this.selectedNewRegionInline = "";
      }
    },

    removeRegion(regionId) {
      const index = this.selectedRegions.indexOf(regionId);
      if (index > -1) {
        this.selectedRegions.splice(index, 1);
      }
    },

    showDropdown(event) {
      const container = event.currentTarget;
      const dropdown = container.querySelector('.dropdown-content');
      
      if (dropdown) {
        // Cancelar qualquer timeout de fechamento pendente
        this.cancelHideDropdown();
        
        // Fechar outros dropdowns abertos
        this.hideAllDropdowns(true);
        
        // Pequeno delay para permitir que outros dropdowns fechem
        setTimeout(() => {
          // Obter posição do elemento trigger
          const rect = container.getBoundingClientRect();
          
          // Calcular posição do dropdown
          let top = rect.bottom + 5;
          let left = rect.left + (rect.width / 2) - 140;
          
          // Verificar se dropdown sai da tela pela direita
          if (left + 280 > window.innerWidth) {
            left = window.innerWidth - 290;
          }
          
          // Verificar se dropdown sai da tela pela esquerda
          if (left < 10) {
            left = 10;
          }
          
          // Verificar se dropdown sai da tela por baixo
          if (top + 250 > window.innerHeight) {
            top = rect.top - 252;
          }
          
          dropdown.style.top = top + 'px';
          dropdown.style.left = left + 'px';
          dropdown.style.display = 'block';
          
          // Trigger animação
          setTimeout(() => {
            dropdown.classList.add('show');
          }, 10);
        }, 50);
      }
    },

    scheduleHideDropdown(event) {
      // Agendar o fechamento do dropdown com delay
      this.hideTimeout = setTimeout(() => {
        this.hideDropdown();
      }, 300);
    },

    cancelHideDropdown() {
      // Cancelar o fechamento agendado quando o mouse retorna
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
        this.hideTimeout = null;
      }
    },

    hideDropdown() {
      // Esconder dropdown atual
      const activeDropdown = document.querySelector('.dropdown-content.show');
      if (activeDropdown) {
        activeDropdown.classList.remove('show');
        setTimeout(() => {
          activeDropdown.style.display = 'none';
        }, 200);
      }
    },

    hideAllDropdowns(immediate = false) {
      // Esconder todos os dropdowns visíveis
      const dropdowns = document.querySelectorAll('.dropdown-content.show');
      dropdowns.forEach(dropdown => {
        dropdown.classList.remove('show');
        if (immediate) {
          dropdown.style.display = 'none';
        } else {
          setTimeout(() => {
            dropdown.style.display = 'none';
          }, 200);
        }
      });
    },

    // Métodos para Modal de Usuário
    openUserModal() {
      this.newUser = {
        name: "",
        username: "",
        phone: "",
        role: "",
        password: ""
      };
      this.showUserModal = true;
    },

    closeUserModal() {
      this.showUserModal = false;
      this.newUser = {
        name: "",
        username: "",
        phone: "",
        role: "",
        password: ""
      };
    },

    validateUser() {
      if (!this.newUser.name || this.newUser.name.trim() === "") {
        toast.error("Nome é obrigatório!");
        return false;
      }
      if (!this.newUser.username || this.newUser.username.trim() === "") {
        toast.error("Nome de usuário é obrigatório!");
        return false;
      }
      if (!this.newUser.password || this.newUser.password.trim() === "") {
        toast.error("Senha é obrigatória!");
        return false;
      }
      if (this.newUser.password && this.newUser.password.length < 6) {
        toast.error("A senha deve ter pelo menos 6 caracteres!");
        return false;
      }
      if (!this.newUser.role) {
        toast.error("Cargo é obrigatório!");
        return false;
      }
      return true;
    },

    async saveUser() {
      if (this.savingUser) return;

      if (this.validateUser()) {
        this.savingUser = true;
        try {
          const userToSend = {
            name: this.newUser.name,
            username: this.newUser.username,
            phone: this.newUser.phone || null,
            role: parseInt(this.newUser.role),
            bancaId: null, // Será associado após criar/salvar a banca
            regionId: null, // Região é opcional
            password: this.newUser.password,
            isActive: true
          };
          
          const response = await mainService.insertUser(userToSend);
          toast.success("Usuário criado com sucesso! Será associado à banca ao salvar.");
          
          // Recarregar lista de usuários
          await this.loadUsers();
          
          // Adicionar o novo usuário automaticamente à lista de responsáveis
          if (response.data && response.data.id) {
            this.selectedResponsibleUsers.push(response.data.id);
            // Guardar o ID do usuário para associar à banca depois
            this.usersCreatedInModal.push(response.data.id);
          }
          
          this.closeUserModal();
        } catch (error) {
          toast.error(handleErrorMessage(error));
        } finally {
          this.savingUser = false;
        }
      }
    },

    // Métodos para Modal de Região
    async openRegionModal() {
      this.showRegionModal = true;
      this.loadingRegionModal = true;
      
      this.newRegion = {
        name: "",
        responsibleId: null,
        descargaRegionId: null,
        onlyDescarga: false
      };
      this.selectedRegionResponsible = null;
      this.regionResponsibleSearchTerm = "";
      this.regionResponsiblesResults = [];
      this.showRegionResponsibleDropdown = false;
      
      this.selectedRegionDescarga = null;
      this.regionDescargaSearchTerm = "";
      this.regionDescargaResults = [];
      this.allRegionDescarga = [];
      this.showRegionDescargaDropdown = false;
      
      try {
        // Recarregar lista de usuários para incluir os recém-criados
        await this.loadUsers();
        // Recarregar lista de regiões também
        await this.loadRegions();
      } catch (error) {
        // Ignorar erro
      } finally {
        this.loadingRegionModal = false;
      }
    },

    closeRegionModal() {
      this.showRegionModal = false;
      this.newRegion = {
        name: "",
        responsibleId: null,
        descargaRegionId: null,
        onlyDescarga: false
      };
      this.selectedRegionResponsible = null;
      this.regionResponsibleSearchTerm = "";
      this.regionResponsiblesResults = [];
      this.showRegionResponsibleDropdown = false;
      
      this.selectedRegionDescarga = null;
      this.regionDescargaSearchTerm = "";
      this.regionDescargaResults = [];
      this.allRegionDescarga = [];
      this.showRegionDescargaDropdown = false;
    },

    validateRegion() {
      if (!this.newRegion.name || this.newRegion.name.trim() === "") {
        toast.error("Nome da região é obrigatório!");
        return false;
      }
      return true;
    },

    async saveRegion() {
      if (this.savingRegion) return;

      if (this.validateRegion()) {
        this.savingRegion = true;
        try {
          const regionToSend = {
            name: this.newRegion.name,
            responsibleId: this.selectedRegionResponsible ? this.selectedRegionResponsible.id : null,
            descargaRegionId: this.selectedRegionDescarga ? this.selectedRegionDescarga.id : null,
            bancaId: null,
            onlyDescarga: this.newRegion.onlyDescarga || false,
            prizeExpirationDays: 7,
            requirePinForPrizeClaim: true,
            milharBonusEnabled: false,
            milharBonusMinValue: 0,
            milharBonusMaxBetValue: 10,
            milharBonusCotacao: 100,
            milharBonusMaxPrize: 1000,
            ticketFooterEnabled: false,
            ticketFooterImage: null,
            ticketFooterText: null,
            isActive: true
          };
          
          const response = await mainService.insertRegion(regionToSend);
          toast.success("Região criada com sucesso!");
          
          // Recarregar lista de regiões
          await this.loadRegions();
          
          // Adicionar a nova região automaticamente à lista
          if (response.data && response.data.id) {
            this.selectedRegions.push(response.data.id);
          }

          this.notifyHeaderSelectsRefresh({ bancas: false, regions: true });
          
          this.closeRegionModal();
        } catch (error) {
          toast.error(handleErrorMessage(error));
        } finally {
          this.savingRegion = false;
        }
      }
    },

    // Métodos de autocomplete para responsável da região
    searchRegionResponsibles() {
      if (this.regionResponsibleSearchTimeout) {
        clearTimeout(this.regionResponsibleSearchTimeout);
      }

      this.regionResponsibleSearchTimeout = setTimeout(async () => {
        if (this.regionResponsibleSearchTerm.trim().length >= 2) {
          this.loadingRegionResponsibles = true;
          this.showRegionResponsibleDropdown = true;

          try {
            const [adminResponse, gerenteResponse, bancaResponse] = await Promise.all([
              mainService.searchUsers({
                name: this.regionResponsibleSearchTerm,
                isActive: true,
                role: 0 // ADMIN
              }),
              mainService.searchUsers({
                name: this.regionResponsibleSearchTerm,
                isActive: true,
                role: 1 // GERENTE
              }),
              mainService.searchUsers({
                name: this.regionResponsibleSearchTerm,
                isActive: true,
                role: 3 // BANCA
              })
            ]);

            const allUsers = [...(adminResponse.data || []), ...(gerenteResponse.data || []), ...(bancaResponse.data || [])];
            this.regionResponsiblesResults = allUsers.filter((user, index, self) => 
              index === self.findIndex(u => u.id === user.id)
            );
          } catch (error) {
            this.regionResponsiblesResults = [];
          } finally {
            this.loadingRegionResponsibles = false;
          }
        } else {
          this.regionResponsiblesResults = [];
          this.showRegionResponsibleDropdown = false;
        }
      }, 300);
    },

    selectRegionResponsible(responsible) {
      this.selectedRegionResponsible = responsible;
      this.regionResponsibleSearchTerm = responsible.name;
      this.showRegionResponsibleDropdown = false;
      this.regionResponsiblesResults = [];
      this.newRegion.responsibleId = responsible.id;
    },

    clearRegionResponsibleSelection() {
      this.selectedRegionResponsible = null;
      this.regionResponsibleSearchTerm = "";
      this.regionResponsiblesResults = [];
      this.showRegionResponsibleDropdown = false;
      this.newRegion.responsibleId = null;
    },

    hideRegionResponsibleDropdown() {
      setTimeout(() => {
        this.showRegionResponsibleDropdown = false;
      }, 150);
    },

    // Métodos de autocomplete para região de descarga
    async loadAllRegionDescarga() {
      if (this.allRegionDescarga.length > 0) {
        this.showRegionDescargaDropdown = true;
        this.applyRegionDescargaLocalFilter();
        return;
      }

      this.showRegionDescargaDropdown = true;
      this.loadingRegionDescarga = true;

      try {
        const response = await mainService.searchRegions({});
        const filteredRegions = (response.data || []).filter(region => region.onlyDescarga === true);
        this.allRegionDescarga = filteredRegions;
        this.applyRegionDescargaLocalFilter();
      } catch (error) {
        this.allRegionDescarga = [];
        this.regionDescargaResults = [];
      } finally {
        this.loadingRegionDescarga = false;
      }
    },

    searchRegionDescarga() {
      this.applyRegionDescargaLocalFilter();
    },

    applyRegionDescargaLocalFilter() {
      const searchTerm = this.regionDescargaSearchTerm.trim().toLowerCase();
      
      if (!searchTerm) {
        this.regionDescargaResults = [...this.allRegionDescarga];
      } else {
        this.regionDescargaResults = this.allRegionDescarga.filter(region => 
          region.name.toLowerCase().includes(searchTerm)
        );
      }
    },

    selectRegionDescarga(region) {
      this.selectedRegionDescarga = region;
      this.regionDescargaSearchTerm = region.name;
      this.showRegionDescargaDropdown = false;
      this.regionDescargaResults = [];
      this.newRegion.descargaRegionId = region.id;
    },

    clearRegionDescargaSelection() {
      this.selectedRegionDescarga = null;
      this.regionDescargaSearchTerm = "";
      this.regionDescargaResults = [];
      this.showRegionDescargaDropdown = false;
      this.newRegion.descargaRegionId = null;
    },

    hideRegionDescargaDropdown() {
      setTimeout(() => {
        this.showRegionDescargaDropdown = false;
      }, 150);
    },

    // Método para associar usuários à banca após criação
    async associateUsersToBanca(bancaId, userIds) {
      try {
        const updatePromises = userIds.map(async (userId) => {
          const user = this.users.find(u => u.id === userId);
          if (user) {
            const userToUpdate = {
              id: userId,
              name: user.name,
              username: user.username,
              phone: user.phone,
              role: user.role,
              bancaId: bancaId, // Associar à banca
              regionId: user.regionId,
              isActive: user.isActive
            };
            
            // Não incluir senha na atualização
            await mainService.updateUser(userToUpdate);
          }
        });

        await Promise.all(updatePromises);
      } catch (error) {
        toast.error('Banca criada, mas houve erro ao associar alguns usuários.');
      }
    }
  }
};
</script>

<style scoped>
/* Alinhamento da tabela */
.modern-table {
  width: 100%;
  text-align: center;
}

.table-container {
  overflow: visible;
}

.table-count {
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 400;
  margin-left: 0.5rem;
  white-space: nowrap;
}

.pagination-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-pagination {
  padding: 0.5rem;
  border: 1px solid var(--card-border, var(--border-color));
  background: var(--bg-primary);
  color: var(--text-primary);
  border-radius: 6px;
  cursor: pointer;
  transition: var(--transition, all 0.2s);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.btn-pagination:hover:not(:disabled) {
  background: var(--accent-primary);
  color: white;
  border-color: var(--accent-primary);
}

.btn-pagination:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-weight: 500;
  color: var(--text-primary);
  min-width: 60px;
  text-align: center;
}

.pagination-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid var(--card-border, var(--border-color));
  background: var(--bg-primary);
}

.modern-table th {
  text-align: center;
  vertical-align: middle;
}

.modern-table td {
  text-align: center;
  vertical-align: middle;
  padding: 12px 8px;
}

/* Primeira coluna - Nome da Banca */
.banca-info {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  text-align: left;
}

.banca-code {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
  color: var(--text-primary);
}

.badge-code {
  background: linear-gradient(135deg, #64748b, #475569);
  color: #fff;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.06em;
  min-width: 3.5rem;
  justify-content: center;
}

.banca-code-input {
  font-weight: 700;
  letter-spacing: 0.08em;
  cursor: not-allowed;
  opacity: 0.85;
}

.banca-name {
  font-weight: 500;
  color: var(--text-primary, #2c3e50);
}

.badge-redescarga {
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #059669;
  background: rgba(5, 150, 105, 0.15);
  border-radius: 6px;
}

.input-hint {
  font-size: 12px;
  color: var(--text-muted, #6b7280);
  margin-top: 4px;
}

/* Terceira coluna - Ações */
.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

/* Estado vazio */
.no-data-message {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 24px;
  color: var(--text-muted, #6c757d);
}

/* Estilos para múltiplos usuários e regiões inline */
.input-group.full-width {
  grid-column: 1 / -1;
}

.selected-users, .selected-regions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 15px;
}

.selected-user-item, .selected-region-item {
  background: #007bff;
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 500;
}

.selected-user-item .user-name,
.selected-region-item .region-name {
  flex: 1;
}

.btn-remove-user-inline, .btn-remove-region-inline {
  background: none;
  border: none;
  color: white;
  font-size: 12px;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.btn-remove-user-inline:hover, .btn-remove-region-inline:hover {
  background: rgba(255, 255, 255, 0.2);
}

.add-user-inline, .add-region-inline {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.user-filter-container {
  position: relative;
  flex: 1;
  min-width: 200px;
}

.user-filter-input-wrapper {
  position: relative;
}

.user-filter-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted, #6c757d);
  pointer-events: none;
  font-size: 0.9rem;
}

.user-select-inline, .region-select-inline {
  width: 100%;
  min-width: 200px;
}

.user-filter-container .user-select-inline {
  padding-left: 36px;
}

.user-filter-list {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 240px;
  overflow-y: auto;
  background: var(--card-background, #fff);
  border: 1px solid var(--border-color, #dee2e6);
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  z-index: 20;
}

.user-filter-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 12px;
  color: var(--text-muted, #6c757d);
  font-size: 0.9rem;
}

.user-filter-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.user-filter-item:hover {
  background: var(--hover-color, #f8f9fa);
}

.user-filter-item > i {
  color: var(--primary-color, #007bff);
}

.user-filter-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.user-filter-name {
  color: var(--text-primary, #212529);
  font-weight: 500;
  font-size: 0.92rem;
}

.user-filter-username {
  color: var(--text-muted, #6c757d);
  font-size: 0.8rem;
}

.regions-count, .users-count {
  color: var(--accent-primary);
  font-weight: 500;
}

.no-data {
  color: var(--text-muted);
  font-style: italic;
}

/* Dropdown Hover Styles */
.dropdown-hover-container {
  position: relative;
  display: inline-block;
}

.hover-trigger {
  cursor: pointer;
  color: var(--text-primary);
  font-weight: 500;
  transition: color 0.2s;
}

.hover-trigger:hover {
  color: var(--accent-primary);
}

.dropdown-content {
  display: none;
  position: fixed;
  background-color: var(--bg-primary);
  min-width: 280px;
  max-width: 350px;
  box-shadow: var(--shadow-lg);
  z-index: 9999;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--border-color);
  opacity: 0;
  transform: translateY(-10px);
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-content::before {
  content: '';
  position: absolute;
  top: -5px;
  left: 0;
  right: 0;
  height: 5px;
  background: transparent;
}

.dropdown-content.show {
  opacity: 1;
  transform: translateY(0);
}

/* Removido hover automático - agora controlado via JavaScript */

.dropdown-header {
  background: var(--accent-primary);
  padding: 12px 15px;
  font-weight: 600;
  color: white;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.dropdown-list {
  padding: 8px 0;
  max-height: 250px;
  overflow-y: auto;
}

.dropdown-item {
  padding: 12px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-primary);
  transition: background-color 0.2s;
  border-bottom: 1px solid var(--border-light);
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background-color: var(--bg-secondary);
}

.dropdown-item i {
  color: var(--text-muted);
  width: 16px;
  text-align: center;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.user-name {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.9rem;
}

.user-role {
  font-size: 0.8rem;
  color: var(--accent-primary);
  font-weight: 500;
}

.user-username {
  font-size: 0.75rem;
  color: var(--text-muted);
}

/* Estilos específicos para tema escuro */
[data-theme="dark"] .dropdown-content {
  background-color: var(--bg-primary);
  border-color: var(--border-color);
  box-shadow: 0px 8px 20px 0px rgba(0,0,0,0.4);
}

[data-theme="dark"] .dropdown-header {
  background: var(--accent-primary);
  border-bottom-color: var(--border-color);
}

[data-theme="dark"] .dropdown-item {
  color: var(--text-primary);
  border-bottom-color: var(--border-light);
}

[data-theme="dark"] .dropdown-item:hover {
  background-color: var(--bg-secondary);
}

[data-theme="dark"] .dropdown-item i {
  color: var(--text-muted);
}

/* Botão de criar rápido */
.btn-quick-add {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
  white-space: nowrap;
}

.btn-quick-add:hover {
  background: linear-gradient(135deg, #218838 0%, #1aa179 100%);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4);
  transform: translateY(-2px);
}

.btn-quick-add:active {
  transform: translateY(0);
}

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Modal Content */
.modal-content {
  background: var(--bg-primary);
  border-radius: 12px;
  box-shadow: var(--shadow-lg);
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Modal Header */
.modal-header {
  background: linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-secondary, #667eea) 100%);
  color: white;
  padding: 20px 24px;
  border-radius: 12px 12px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-close-modal {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.btn-close-modal:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

/* Modal Body */
.modal-body {
  padding: 24px;
}

/* Modal Loading */
.modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  gap: 15px;
  color: var(--text-muted);
}

.modal-loading i {
  font-size: 2rem;
  color: var(--accent-primary, #007bff);
}

.modal-loading span {
  font-size: 0.95rem;
  font-weight: 500;
}

.modal-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.modal-input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.modal-input-group.full-width {
  grid-column: 1 / -1;
}

/* Modal Footer */
.modal-footer {
  padding: 20px 24px;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* Modal Large */
.modal-large {
  max-width: 700px;
}

/* Info Box */
.info-box {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.1) 0%, rgba(0, 123, 255, 0.05) 100%);
  border-left: 4px solid var(--accent-primary, #007bff);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 0.9rem;
}

.info-box i {
  color: var(--accent-primary, #007bff);
  font-size: 1.1rem;
  flex-shrink: 0;
}

[data-theme="dark"] .info-box {
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.15) 0%, rgba(0, 123, 255, 0.08) 100%);
}

/* Modal Form Grid Single Column */
.modal-form-grid-single {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Autocomplete Container */
.autocomplete-container {
  position: relative;
}

.autocomplete-input {
  width: 100%;
  padding-right: 40px;
}

.clear-selection-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.clear-selection-btn:hover {
  color: var(--danger-color, #dc3545);
}

/* Autocomplete Dropdown */
.autocomplete-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  box-shadow: var(--shadow-lg);
  max-height: 250px;
  overflow-y: auto;
  z-index: 1000;
  margin-top: 5px;
}

.autocomplete-item {
  padding: 12px 15px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid var(--border-light);
}

.autocomplete-item:last-child {
  border-bottom: none;
}

.autocomplete-item:hover {
  background-color: var(--bg-secondary);
}

.responsible-info,
.region-info-dropdown {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.responsible-name,
.region-name-dropdown {
  font-weight: 500;
  color: var(--text-primary);
}

.responsible-username,
.region-type-dropdown {
  font-size: 0.85rem;
  color: var(--text-muted);
}

.autocomplete-empty,
.autocomplete-loading {
  padding: 15px;
  text-align: center;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

/* Checkbox Group */
.checkbox-group {
  margin-top: 10px;
}

.checkbox-container {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  position: relative;
  padding: 12px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
}

.checkbox-container:hover {
  background-color: var(--bg-secondary, #f8f9fa);
}

.checkbox-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.checkbox-checkmark {
  height: 20px;
  width: 20px;
  background-color: var(--bg-primary);
  border: 2px solid var(--border-color);
  border-radius: 4px;
  position: relative;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.checkbox-input:checked ~ .checkbox-checkmark {
  background-color: var(--accent-primary, #007bff);
  border-color: var(--accent-primary, #007bff);
}

.checkbox-checkmark:after {
  content: "";
  position: absolute;
  display: none;
  left: 6px;
  top: 2px;
  width: 6px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.checkbox-input:checked ~ .checkbox-checkmark:after {
  display: block;
}

.checkbox-label {
  font-weight: 500;
  color: var(--text-primary);
  user-select: none;
}

.checkbox-description {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 12px;
  background-color: rgba(0, 123, 255, 0.1);
  border-radius: 6px;
  font-size: 0.85em;
  color: var(--text-muted);
}

.checkbox-description i {
  flex-shrink: 0;
  color: var(--accent-primary, #007bff);
}

/* Responsividade */
@media (max-width: 768px) {
  .user-select-inline, .region-select-inline {
    min-width: auto;
  }

  .selected-users, .selected-regions {
    flex-direction: column;
    gap: 8px;
  }

  .search-container {
    flex-direction: column;
    align-items: stretch;
  }

  .dropdown-content {
    min-width: 260px;
    max-width: 90vw;
  }

  .modern-table {
    font-size: 0.85rem;
  }

  .modern-table th,
  .modern-table td {
    padding: 8px 4px;
  }

  .add-user-inline, .add-region-inline {
    flex-direction: column;
  }

  .btn-quick-add {
    width: 100%;
    justify-content: center;
  }

  .modal-content {
    width: 95%;
    max-height: 95vh;
  }

  .modal-form-grid {
    grid-template-columns: 1fr;
  }

  .modal-footer {
    flex-direction: column-reverse;
  }

  .modal-footer button {
    width: 100%;
  }

  .pagination-footer {
    flex-direction: column;
    gap: 1rem;
  }
}
</style> 